// Bookie · Sage ("Mehrli Bog'") — preview thumbnail. The full
// invitation lives at /templates/bookie-sage.html (calm,
// botanical, garden-wedding feel: sage-bg #EEF0E8 base + sage
// #7A8268 accent + cream paper-2, Fraunces display + Cormorant
// Garamond italic body + Allura script, eucalyptus stems in the
// hero corners + a single olive sprig under every section
// eyebrow). The catalog card is a 5:7 echo of that mood: paired
// eucalyptus stems at top, the eyebrow, a soft serif headline,
// a thin sage rule, then the couple's names. Photo-free; all
// botanical ornaments are inline SVG so the card scales without
// ever loading a bitmap.

import type { LayoutCtx } from "./_shared";

export function BookieSagePreviewLayout({ names, date, venue, accent, ink, bg, font }: LayoutCtx) {
  const [a, b] = names.split(/\s*&\s*/);
  // Sage stem ink — kept independent of the catalog row's
  // `accent` so the eucalyptus always reads "garden-calm" even
  // when the row swaps in a warmer accent for variety.
  const sage = "#7A8268";
  const sageSoft = "#A6AC93";
  const sageBg = "#EEF0E8";
  const cream = "#F5F1E6";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${cream} 0%, ${bg} 36%, ${sageBg} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "12% 9% 9%",
      }}
    >
      {/* Eucalyptus stems — paired in the top corners, mirroring the HTML hero */}
      <svg
        aria-hidden
        viewBox="0 0 130 200"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "30%",
          height: "auto",
          color: sage,
          opacity: 0.9,
        }}
      >
        <path d="M30 -4 Q 28 60 36 110 Q 44 158 58 196" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.85" />
        <g fill="currentColor">
          <ellipse cx="22" cy="22" rx="11" ry="4" transform="rotate(-30 22 22)" opacity="0.65" />
          <ellipse cx="42" cy="34" rx="11" ry="4" transform="rotate(30 42 34)" opacity="0.65" />
          <ellipse cx="20" cy="54" rx="12" ry="4.4" transform="rotate(-28 20 54)" opacity="0.7" />
          <ellipse cx="46" cy="66" rx="12" ry="4.4" transform="rotate(28 46 66)" opacity="0.7" />
          <ellipse cx="22" cy="88" rx="11" ry="4" transform="rotate(-26 22 88)" opacity="0.6" />
          <ellipse cx="48" cy="100" rx="11" ry="4" transform="rotate(26 48 100)" opacity="0.6" />
          <ellipse cx="26" cy="122" rx="10" ry="3.6" transform="rotate(-24 26 122)" opacity="0.55" />
          <ellipse cx="52" cy="136" rx="10" ry="3.6" transform="rotate(24 52 136)" opacity="0.55" />
        </g>
      </svg>
      <svg
        aria-hidden
        viewBox="0 0 130 200"
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "30%",
          height: "auto",
          color: sage,
          opacity: 0.9,
          transform: "scaleX(-1)",
        }}
      >
        <path d="M30 -4 Q 28 60 36 110 Q 44 158 58 196" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.85" />
        <g fill="currentColor">
          <ellipse cx="22" cy="22" rx="11" ry="4" transform="rotate(-30 22 22)" opacity="0.65" />
          <ellipse cx="42" cy="34" rx="11" ry="4" transform="rotate(30 42 34)" opacity="0.65" />
          <ellipse cx="20" cy="54" rx="12" ry="4.4" transform="rotate(-28 20 54)" opacity="0.7" />
          <ellipse cx="46" cy="66" rx="12" ry="4.4" transform="rotate(28 46 66)" opacity="0.7" />
          <ellipse cx="22" cy="88" rx="11" ry="4" transform="rotate(-26 22 88)" opacity="0.6" />
          <ellipse cx="48" cy="100" rx="11" ry="4" transform="rotate(26 48 100)" opacity="0.6" />
          <ellipse cx="26" cy="122" rx="10" ry="3.6" transform="rotate(-24 26 122)" opacity="0.55" />
          <ellipse cx="52" cy="136" rx="10" ry="3.6" transform="rotate(24 52 136)" opacity="0.55" />
        </g>
      </svg>

      {/* Eyebrow */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.42em",
          textTransform: "uppercase",
          color: sage,
          marginTop: 12,
          zIndex: 1,
        }}
      >
        MEHRLI BOG&apos;
      </div>

      {/* Olive sprig under the eyebrow — the section-eyebrow motif from the HTML */}
      <svg
        aria-hidden
        width="42"
        height="12"
        viewBox="0 0 46 14"
        style={{ marginTop: 6, color: sage, opacity: 0.9, zIndex: 1 }}
      >
        <path d="M2 7 Q 23 5 44 7" stroke="currentColor" strokeWidth="0.7" fill="none" />
        <ellipse cx="10" cy="6" rx="3.4" ry="1.4" transform="rotate(-22 10 6)" fill="currentColor" opacity="0.85" />
        <ellipse cx="14" cy="9" rx="3.4" ry="1.4" transform="rotate(22 14 9)" fill="currentColor" opacity="0.85" />
        <ellipse cx="32" cy="6" rx="3.4" ry="1.4" transform="rotate(22 32 6)" fill="currentColor" opacity="0.85" />
        <ellipse cx="36" cy="9" rx="3.4" ry="1.4" transform="rotate(-22 36 9)" fill="currentColor" opacity="0.85" />
        <circle cx="23" cy="7" r="1.1" fill="currentColor" />
      </svg>

      {/* Headline — Fraunces, soft display */}
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
          fontSize: 21,
          lineHeight: 1.05,
          color: ink,
          marginTop: 10,
          maxWidth: "100%",
          zIndex: 1,
        }}
      >
        Bog&apos; qo&apos;ynida
      </div>

      {/* Sage rule */}
      <div
        aria-hidden
        style={{
          width: 28,
          height: 1,
          background: sage,
          opacity: 0.7,
          margin: "10px 0 6px",
          zIndex: 1,
        }}
      />

      {/* Allura flourish */}
      <div
        style={{
          fontFamily: "'Allura', cursive",
          fontSize: 16,
          color: sage,
          lineHeight: 1.1,
          zIndex: 1,
        }}
      >
        sokin &amp; mehrli
      </div>

      {/* Couple names */}
      <div
        style={{
          marginTop: 12,
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
          fontSize: 14,
          color: ink,
          lineHeight: 1.2,
          zIndex: 1,
        }}
      >
        {a ?? names}
      </div>
      <div
        style={{
          fontFamily: "'Allura', cursive",
          fontSize: 18,
          color: sageSoft,
          lineHeight: 1,
          margin: "1px 0",
          zIndex: 1,
        }}
      >
        &amp;
      </div>
      <div
        style={{
          fontFamily: "'Fraunces', serif",
          fontWeight: 400,
          fontSize: 14,
          color: ink,
          lineHeight: 1.2,
          zIndex: 1,
        }}
      >
        {b ?? ""}
      </div>

      {/* Date + venue footer — accent color stays muted so the botanical mood
          isn't broken by a loud date stamp */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 12,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: ink,
          opacity: 0.75,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
          zIndex: 1,
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 10,
          color: ink,
          opacity: 0.7,
          marginTop: 2,
          zIndex: 1,
        }}
      >
        {venue}
      </div>

      {/* Silence unused-var lints — `accent` arrives via LayoutCtx but the
          sage palette is fixed for this theme */}
      <span style={{ display: "none" }}>{accent}</span>
    </div>
  );
}
